import { useState } from "react";

interface Feature {
	emoji: string;
	title: string;
	description: string;
	gradientHover: string;
}

const features: Feature[] = [
	{
		emoji: "🏥",
		title: "Qualified Professionals",
		description: "Vetted doctors, nurses, and caregivers with verified credentials and experience.",
		gradientHover: "linear-gradient(135deg, #e0f7f5 0%, #b2ebf2 100%)",
	},
	{
		emoji: "📱",
		title: "Easy Booking",
		description: "Book appointments in seconds with real-time availability and instant confirmation.",
		gradientHover: "linear-gradient(135deg, #e0f7f5 0%, #b2ebf2 100%)",
	},
	{
		emoji: "🏠",
		title: "Home Care Services",
		description: "Receive quality medical care in the comfort and safety of your own home.",
		gradientHover: "linear-gradient(135deg, #e0f7f5 0%, #b2ebf2 100%)",
	},
	{
		emoji: "💳",
		title: "Flexible Payment",
		description: "Multiple payment options including cash, card, and insurance coverage.",
		gradientHover: "linear-gradient(135deg, #e0f7f5 0%, #b2ebf2 100%)",
	},
	{
		emoji: "📊",
		title: "Health Monitoring",
		description: "Track your health metrics, medications, and appointments all in one place.",
		gradientHover: "linear-gradient(135deg, #e0f7f5 0%, #b2ebf2 100%)",
	},
	{
		emoji: "🚨",
		title: "Emergency Support",
		description: "24/7 emergency request system for urgent medical situations.",
		gradientHover: "linear-gradient(135deg, #e0f7f5 0%, #b2ebf2 100%)",
	},
];

export default function Features() {
	const [hovered, setHovered] = useState<number | null>(null);

	return (
		<section id="features" className="bg-white w-full">
			<div className="max-w-6xl mx-auto px-6 py-20 flex flex-col gap-12">

				{/* Header */}
				<div className="flex flex-col items-center text-center gap-3">
                    <span className="text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wide" style={{ backgroundColor: "#b2ebf2", color: "#2C8C87" }}>
 						Key Features
					</span>
					<h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
						Everything <span style={{ color: "#2C8C87" }}>You Need</span>
					</h2>
					<p className="text-gray-500 text-base max-w-md leading-relaxed">
						Comprehensive healthcare features at your fingertips
					</p>
				</div>

				{/* Grid */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
					{features.map((feature, index) => {
						const isHovered = hovered === index;
						const isDimmed = hovered !== null && !isHovered;

						return (
							<div
								key={feature.title}
								onMouseEnter={() => setHovered(index)}
								onMouseLeave={() => setHovered(null)}
								className="flex flex-col gap-4 p-6 rounded-2xl border cursor-default"
								style={{
									background: isHovered
										? feature.gradientHover
										: "linear-gradient(135deg, #f8fdfd 0%, #eef8f7 100%)",
									borderColor: isHovered ? "#76C7C0" : "#e4eeee",
									boxShadow: isHovered
										? "0 8px 30px rgba(44, 140, 135, 0.18)"
										: "0 1px 6px rgba(0,0,0,0.05)",
									opacity: isDimmed ? 0.5 : 1,
									transform: isHovered ? "scale(1.04)" : "scale(1)",
									transition: "all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
								}}
							>
								{/* Icon */}
								<div
									className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
									style={{
										background: isHovered
											? "linear-gradient(135deg, #ffffff 0%, #d4f5f2 100%)"
											: "linear-gradient(135deg, #ffffff 0%, #f0fafa 100%)",
										boxShadow: isHovered
											? "0 4px 12px rgba(44, 140, 135, 0.2)"
											: "0 2px 6px rgba(0,0,0,0.06)",
										transition: "all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
									}}
								>
									{feature.emoji}
								</div>

								{/* Text */}
								<div className="flex flex-col gap-1.5">
									<h3
										className="text-base font-semibold"
										style={{
											color: isHovered ? "#2C8C87" : "#1a1a1a",
											transition: "color 0.5s ease",
										}}
									>
										{feature.title}
									</h3>
									<p
										className="text-sm leading-relaxed"
										style={{
											color: isHovered ? "#3a7a76" : "#6b7280",
											transition: "color 0.5s ease",
										}}
									>
										{feature.description}
									</p>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
}